import React, { useState } from 'react';
import { motion } from 'motion/react';
import '../style.css';
import { Link } from 'react-router-dom';
import {
  fadeUp,
  fadeLeft,
  fadeRight,
  fadeIn,
  staggerContainer,
  scaleFade,
  viewport,
} from '../components/useScrollAnimation';

const highlights = [
  {
    emoji: '💰',
    title: 'Biaya Sangat Terjangkau',
    desc: '50% lebih murah dibandingkan kuliah reguler di universitas Australia karena berstatus pertukaran mahasiswa.',
  },
  {
    emoji: '🎓',
    title: 'Credit Transfer & Belajar Praktikal',
    desc: 'Mata kuliah bisa ditransfer ke universitasmu di Indonesia. Pembelajaran menggunakan The Southern Cross Model — penilaian berbasis praktikal, bukan ujian tradisional.',
  },
  {
    emoji: '🤝',
    title: 'Jaringan Magang Luas & Terjamin',
    desc: 'Penempatan magang dijamin oleh kampus ke mitra bergengsi: hotel, cruise, airlines, theme parks, dan lainnya — disesuaikan minat & bakat kamu.',
  },
  {
    emoji: '💵',
    title: 'Double Income Opportunity',
    desc: 'Minimum salary AUD 27.44 /jam, plus izin kerja part-time reguler hingga 24 jam/minggu di luar jadwal magang.',
  },
  // {
  //   emoji: '🏆',
  //   title: 'Kualitas & Lulusan Terbukti',
  //   desc: '97% lulusan mendapat pekerjaan dalam 6 bulan setelah kelulusan. Dinobatkan #1 untuk Dukungan Mahasiswa di bidang Pariwisata, Perhotelan & Rekreasi.',
  // },
  {
    emoji: '🌏',
    title: 'Pengalaman Internasional',
    desc: 'Mahasiswa mendapatkan pengalaman hidup, belajar, dan bekerja langsung di industri global, membangun mental global, kemampuan bahasa Inggris profesional.',
  },
];

const programs = [
  {
    badge: 'Option 1',
    title: 'Study & Work',
    duration: '1 Semester — 5 Bulan',
    tagline: 'Pilihan tepat untuk pengalaman study abroad singkat dengan penghasilan maksimal.',
    cost: 'AUD 6,930',
    structure: '2 Academic Units + 1 Internship Unit',
    internship: 'Min. 300 jam (±15 minggu)',
    wage: '± AUD 27/jam',
    income: '± AUD 13,932',
    incomeNote: 'Estimasi gabungan gaji magang & part-time',
    accent: false,
  },
  {
    badge: 'Option 2',
    title: 'Study & Work',
    duration: '1 Tahun — 12 Bulan',
    tagline: 'Program komprehensif dengan magang lebih panjang untuk pengalaman industri yang lebih matang.',
    cost: 'AUD 11,550',
    structure: '3 Academic Units + 2 Internship Units',
    internship: 'Min. 600 jam (±30 minggu)',
    wage: '± AUD 27/jam',
    income: '± AUD 30,456',
    incomeNote: 'Estimasi gabungan gaji magang & part-time',
    accent: false,
  },
];

const requirements = [
  {
    icon: 'fa-solid fa-user-graduate',
    label: 'Status Akademik',
    detail: 'Terdaftar sebagai mahasiswa aktif di universitas asal.',
  },
  {
    icon: 'fa-solid fa-calendar-check',
    label: 'Minimal Studi',
    detail: 'Telah menyelesaikan minimal 1 tahun (2 semester) di tingkat universitas.',
  },
  {
    icon: 'fa-solid fa-language',
    label: 'Syarat Bahasa',
    detail: 'Minimum IELTS 6.0 — tidak ada band di bawah 5.5.',
  },
  {
    icon: 'fa-solid fa-file-lines',
    label: 'Dokumen',
    detail: 'Transkrip nilai (legalisir), hasil tes bahasa Inggris, dan CV/Resume.',
  },
];

const intakes = [
  { term: 'Term 1', month: 'Maret' },
  { term: 'Term 3', month: 'Juli' },
  { term: 'Term 4', month: 'Agustus' },
  { term: 'Term 5', month: 'Oktober' },
];

const StudyAbroad = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <section id="study-abroad" className="sa-section">

      {/* ── HERO ── */}
      <div className="sa-hero">
        <div className="sa-hero-overlay" />
        <div className="container sa-hero-content">

          {/* Eyebrow badges */}
          <motion.div
            className="sa-hero-eyebrow"
            variants={fadeLeft}
            initial="hidden"
            animate="show"
          >
            <span className="sa-badge">The Hotel School Australia</span>
            <span className="sa-badge sa-badge-outline">Southern Cross University</span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            className="sa-hero-headline"
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.15 }}
          >
            Study Abroad &amp; Paid Internship<br className="d-none d-md-block" /> ke Australia!
          </motion.h1>

          {/* Sub paragraph */}
          <motion.p
            className="sa-hero-sub"
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.28 }}
          >
            Dapatkan pengalaman kuliah di perhotelan bergengsi sekaligus kerja magang berbayar di Australia.
            Bangun karier internasionalmu bersama <strong>The Hotel School</strong>.
          </motion.p>

          {/* City tags — stagger */}
          <motion.div
            className="sa-city-tags"
            variants={staggerContainer(0.1, 0.38)}
            initial="hidden"
            animate="show"
          >
            {['Sydney', 'Melbourne', 'Brisbane', 'Perth'].map((city) => (
              <motion.span key={city} className="sa-city-tag" variants={scaleFade}>
                <i className="fa-solid fa-location-dot" /> {city}
              </motion.span>
            ))}
          </motion.div>

          {/* CTA buttons */}
          <motion.div
            className="sa-hero-cta"
            variants={staggerContainer(0.14, 0.55)}
            initial="hidden"
            animate="show"
          >
            <motion.div variants={fadeUp}>
              <Link to="/#register" className="sa-btn-primary">
                Daftar Sekarang
              </Link>
            </motion.div>
            <motion.div variants={fadeUp}>
              <a href="#sa-programs" className="sa-btn-ghost">
                Lihat Program
              </a>
            </motion.div>
          </motion.div>

          {/* Urgency line */}
          <motion.p
            className="sa-hero-urgency"
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.72 }}
          >
            ⚡ Kuota terbatas setiap intake — amankan posisimu sekarang!
          </motion.p>
        </div>
        <div className="sa-hero-scroll-hint">
          <i className="fa-solid fa-chevron-down" />
        </div>
      </div>

      {/* ── KEY HIGHLIGHTS ── */}
      <div className="sa-highlights-wrapper">
        <div className="container">

          <motion.div
            className="sa-section-header"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
          >
            <span className="sa-section-label">Key Highlights</span>
            <h2 className="sa-section-title">Kenapa Memilih Program Ini?</h2>
            <p className="sa-section-desc">
              Lima alasan kuat yang membuat program ini berbeda dari program study abroad lainnya.
            </p>
          </motion.div>

          <motion.div
            className="sa-highlights-grid"
            variants={staggerContainer(0.12, 0.1)}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
          >
            {highlights.map((h, i) => (
              <motion.div key={i} className="sa-highlight-card" variants={scaleFade}>
                <div className="sa-highlight-emoji">{h.emoji}</div>
                <h3 className="sa-highlight-title">{h.title}</h3>
                <p className="sa-highlight-desc">{h.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* ── PROGRAMS ── */}
      <div id="sa-programs" className="sa-programs-wrapper">
        <div className="container">

          <motion.div
            className="sa-section-header"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
          >
            <span className="sa-section-label">Pilihan Program</span>
            <h2 className="sa-section-title">Pilihan Program &amp; Estimasi Penghasilan</h2>
            <p className="sa-section-desc">
              Pilih durasi yang paling sesuai dengan rencana studimu.
            </p>
          </motion.div>

          {/* Tab switcher */}
          <motion.div
            className="sa-tab-row"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
          >
            {programs.map((p, i) => (
              <button
                key={i}
                className={`sa-tab-btn${activeTab === i ? ' sa-tab-active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {p.badge}: {p.duration}
              </button>
            ))}
          </motion.div>

          {/* Cards */}
          <motion.div
            className="sa-programs-grid"
            variants={staggerContainer(0.18, 0.1)}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
          >
            {programs.map((p, i) => (
              <motion.div
                key={i}
                className={`program-card${p.accent ? ' program-card-accent' : ''}${activeTab === i ? ' program-card-active' : ''}`}
                onClick={() => setActiveTab(i)}
                variants={scaleFade}
              >
                <div className="program-badge">{p.badge}</div>
                <h3 className="program-title">{p.title}</h3>
                <p className="program-duration">{p.duration}</p>
                <p className="program-tagline">{p.tagline}</p>
                <hr className="divider" />
                <div className="program-details">
                  <div className="detail-row">
                    <span className="detail-label"><i className="fa-solid fa-dollar-sign" /> Biaya</span>
                    <span className="detail-value">{p.cost}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label"><i className="fa-solid fa-book" /> Struktur</span>
                    <span className="detail-value">{p.structure}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label"><i className="fa-solid fa-briefcase" /> Durasi Magang</span>
                    <span className="detail-value">{p.internship}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label"><i className="fa-solid fa-clock" /> Estimasi Upah</span>
                    <span className="detail-value">{p.wage}</span>
                  </div>
                </div>
                <div className="income-box">
                  <span className="income-label">Total Estimasi Penghasilan</span>
                  <span className="income-amount">{p.income}</span>
                  <span className="income-note">{p.incomeNote}</span>
                </div>
                {/* <a href="#sa-register" className={`sa-btn-card${p.accent ? ' sa-btn-card-accent' : ''}`}>
                  Pilih Program Ini
                </a> */}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* ── REQUIREMENTS ── */}
      <div className="sa-requirements-wrapper">
        <div className="container">
          <div className="sa-req-inner">

            {/* Left — requirements */}
            <motion.div
              className="sa-req-left"
              variants={fadeLeft}
              initial="hidden"
              whileInView="show"
              viewport={viewport}
            >
              <span className="sa-section-label sa-section-label-light">Persyaratan</span>
              <h2 className="sa-section-title sa-title-white">Persyaratan Utama</h2>
              <p className="sa-req-subtitle">
                Pastikan kamu memenuhi semua persyaratan berikut sebelum mendaftar.
              </p>
              <motion.div
                className="sa-req-grid"
                variants={staggerContainer(0.12, 0.2)}
                initial="hidden"
                whileInView="show"
                viewport={viewport}
              >
                {requirements.map((r, i) => (
                  <motion.div key={i} className="sa-req-card" variants={scaleFade}>
                    <div className="sa-req-icon">
                      <i className={r.icon} />
                    </div>
                    <div>
                      <p className="sa-req-label">{r.label}</p>
                      <p className="sa-req-detail">{r.detail}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right — intakes */}
            <motion.div
              className="sa-req-right"
              variants={fadeRight}
              initial="hidden"
              whileInView="show"
              viewport={viewport}
            >
              <span className="sa-section-label sa-section-label-light">Jadwal Intake</span>
              <h2 className="sa-section-title sa-title-white">Jadwal Masuk (Intake)</h2>
              <p className="sa-req-subtitle">
                Program dibuka empat kali dalam setahun. Pilih intake yang paling cocok.
              </p>
              <motion.div
                className="sa-intake-grid"
                variants={staggerContainer(0.13, 0.25)}
                initial="hidden"
                whileInView="show"
                viewport={viewport}
              >
                {intakes.map((item, i) => (
                  <motion.div key={i} className="sa-intake-card" variants={scaleFade}>
                    <div className="sa-intake-term">{item.term}</div>
                    <div className="sa-intake-month">{item.month}</div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

          </div>
        </div>
      </div>

      {/* ── CALL TO ACTION ── */}
      <div id="sa-register" className="sa-cta-wrapper">
        {/* <div className="sa-cta-deco sa-cta-deco-1" /> */}
        <div className="container">
          <motion.div
            className="sa-cta-inner"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
          >
            {/* <div className="sa-cta-deco sa-cta-deco-2" /> */}
            <span className="sa-section-label">Langkah Pertamamu</span>
            <h2 className="sa-cta-title">
              Siap Memulai Perjalanan<br />Internasionalmu?
            </h2>
            <p className="sa-cta-desc">
              Kuota terbatas untuk setiap intake! Konsultasikan rencana studimu sekarang dan
              amankan posisimu di <strong>The Hotel School Australia</strong>.
            </p>
            <motion.div
              className="sa-cta-actions"
              variants={staggerContainer(0.15, 0.2)}
              initial="hidden"
              whileInView="show"
              viewport={viewport}
            >
              <motion.a
                href="https://wa.me/6281234567890?text=Halo%2C%20saya%20ingin%20konsultasi%20mengenai%20program%20Study%20Abroad%20The%20Hotel%20School%20Australia"
                target="_blank"
                rel="noreferrer"
                className="sa-btn-primary sa-btn-lg"
                variants={scaleFade}
              >
                <i className="fa-brands fa-whatsapp" /> Konsultasi Gratis
              </motion.a>
              <motion.a
                href="/#register"
                className="sa-btn-outline sa-btn-lg"
                variants={scaleFade}
              >
                Daftar Sekarang
              </motion.a>
            </motion.div>
            <p className="sa-cta-note">
              🔒 Data kamu aman. Kami tidak akan membagikan informasi pribadimu.
            </p>
          </motion.div>
        </div>
      </div>

    </section>
  );
};

export default StudyAbroad;
